import {
	Alert,
	Box,
	Stack,
	Text,
	Icons,
	Inline,
	Button,
} from "@sembark-travel/ui-base"
import { XHRError } from "@sembark-travel/xhr"

interface IApiErrorResponse {
	message: string
	errors?: { [key: string]: Array<string> }
	status_code?: number
}

// Section configuration for categorizing errors
const ERROR_SECTIONS = {
	general: {
		title: "General Information",
		icon: <Icons.Info />,
		fields: ["no_of_adults", "children", "days", "comments"],
	},
	hotels: {
		title: "Hotels",
		icon: <Icons.Building />,
		fields: ["hotel_options"],
	},
	flights: {
		title: "Flights",
		icon: <Icons.Airplane />,
		fields: ["flights"],
	},
	visas: {
		title: "Visas",
		icon: <Icons.Visa />,
		fields: ["visas"],
	},
	cabs: {
		title: "Transport/Activity Services",
		icon: <Icons.Taxi />,
		fields: ["cabs"],
	},
	transport_extras: {
		title: "Transport Extras",
		icon: <Icons.Taxi />,
		fields: ["transportExtras"],
	},
	other_extras: {
		title: "Other Extras",
		icon: <Icons.Plus />,
		fields: ["otherExtras"],
	},
	pricing: {
		title: "Pricing & Currency",
		icon: <Icons.BankNotes />,
		fields: [
			"options",
			"pricing_strategy",
			"currency_pairs",
			"tax_name",
			"functional_currency",
			"given_currency",
			"costing_currency",
		],
	},
	other: {
		title: "Other Issues",
		icon: <Icons.Attention />,
		fields: [],
	},
} as const

/**
 * Categorizes field errors by sections based on field names
 */
function categorizeErrors(errors: { [key: string]: Array<string> }) {
	const categorized: Record<
		string,
		Array<{ field: string; messages: Array<string> }>
	> = {}

	Object.entries(errors).forEach(([fieldName, messages]) => {
		let sectionKey = "other"

		// Find matching section based on field name patterns
		for (const [key, section] of Object.entries(ERROR_SECTIONS)) {
			if (section.fields.some((field) => fieldName.includes(field))) {
				sectionKey = key
				break
			}
		}

		if (!categorized[sectionKey]) {
			categorized[sectionKey] = []
		}

		categorized[sectionKey].push({
			field: fieldName,
			messages,
		})
	})

	return categorized
}

/**
 * Formats field names for display
 */
function formatFieldName(fieldName: string): string {
	// Convert field names like "options.0.given_price" to "Option 1 - Given Price"
	return fieldName
		.replace(/options\.(\d+)/, (_, num) => `Option ${parseInt(num) + 1}`)
		.replace(/hotels\.(\d+)/, (_, num) => `Primary Hotel ${parseInt(num) + 1}`)
		.replace(
			/similar_hotel_options\.(\d+)/,
			(_, num) => `Similar Hotel ${parseInt(num) + 1}`
		)
		.replace(/cabs\.(\d+)/, (_, num) => `Transport ${parseInt(num) + 1}`)
		.replace(/flights\.(\d+)/, (_, num) => `Flight ${parseInt(num) + 1}`)
		.replace(
			/date_wise_prices\.(\d+)/,
			(_, num) => `Day Wise Price ${parseInt(num) + 1}`
		)
		.replace(/_/g, " ")
		.replace(/\./g, " - ")
		.replace(/\b\w/g, (l) => l.toUpperCase())
}

/**
 * NewQuote Error Display Component
 * Displays API errors in a structured, beautiful UI format
 */
export function NewQuoteErrorDisplay({
	error,
	onDismiss,
}: {
	error: XHRError | null
	onDismiss?: () => void
}) {
	if (!error) return null

	const errorData = error.response?.data as IApiErrorResponse | undefined
	const mainMessage = errorData?.message || error.message
	const fieldErrors = errorData?.errors || {}

	if (!mainMessage && Object.keys(fieldErrors).length === 0) {
		return null
	}

	const categorizedErrors = categorizeErrors(fieldErrors)
	const hasFieldErrors = Object.keys(categorizedErrors).length > 0

	return (
		<Box {...{ "data-error-display": true }}>
			<Stack gap="4">
				{/* Main Error Message */}
				{mainMessage && (
					<Alert status="error">
						<Inline alignItems={"start"} gap="3">
							<Box flex="1">
								<Stack gap="1">
									<Text fontWeight="semibold">Quote Submission Failed</Text>
									<Text>{mainMessage}</Text>
								</Stack>
							</Box>
							{onDismiss && (
								<Button
									size="sm"
									level="tertiary"
									onClick={onDismiss}
									title="Dismiss error"
								>
									<Icons.Cancel />
								</Button>
							)}
						</Inline>
					</Alert>
				)}

				{/* Section-based Field Errors */}
				{hasFieldErrors && (
					<Stack gap="2">
						<Text fontSize="sm" fontWeight="semibold" color="muted">
							Please fix the following issues:
						</Text>

						<Stack gap="2">
							{Object.entries(categorizedErrors).map(
								([sectionKey, sectionErrors]) => {
									const section =
										ERROR_SECTIONS[sectionKey as keyof typeof ERROR_SECTIONS]

									return (
										<Alert key={sectionKey} status="error">
											<Stack gap="2">
												{/* Section Header */}
												<Inline alignItems="center" gap="2">
													<Text fontWeight="semibold">{section.title}</Text>
													{section.icon}
												</Inline>

												{/* Section Errors */}
												<Stack gap="1" paddingLeft="6">
													{sectionErrors.map(({ field, messages }, index) => (
														<Stack gap="2" key={`${field}-${index}`}>
															<Text
																fontSize="sm"
																fontWeight="semibold"
																color="muted"
															>
																{formatFieldName(field)}:
															</Text>
															<Stack gap="1" paddingLeft="2">
																{messages.map((message, msgIndex) => (
																	<Text
																		key={msgIndex}
																		fontSize="sm"
																		fontWeight="semibold"
																	>
																		• {message}
																	</Text>
																))}
															</Stack>
														</Stack>
													))}
												</Stack>
											</Stack>
										</Alert>
									)
								}
							)}
						</Stack>
					</Stack>
				)}
			</Stack>
		</Box>
	)
}
